import React, {useCallback, useEffect, useRef} from "react";
import {Box, NumberInput, Text, TextInput, Combobox, useCombobox} from "@mantine/core";
import {IPortConfigStep, IPortMap} from "../../../types/protocol";
import {IPortConfigStepDefinition} from "../../../types/protocolDefinitions";
import stepClasses from "../StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {TWorkflow} from "../StepView";
import classes from "./PortConfigStepView.module.css";

/** Union type for port config steps (both execution and planning modes) */
type PortConfigStep = IPortConfigStep | IPortConfigStepDefinition;

interface PortComboboxProps {
    testId: string;
    value: string;
    disabled: boolean;
    onCommit: (value: string) => void;
    onBlur?: (value?: string) => void;
    isPlanningMode: boolean;
    isReadOnly: boolean;
    portRef?: (el: HTMLInputElement | null) => void;
}

/**
 * Combobox component for selecting or entering a port label.
 * Provides predefined options (A, B, C, D) but allows custom input.
 */
const PortCombobox: React.FC<PortComboboxProps> = ({
    testId,
    value,
    disabled,
    onCommit,
    onBlur,
    isPlanningMode,
    isReadOnly,
    portRef,
}) => {
    const combobox = useCombobox();
    const [searchValue, setSearchValue] = React.useState(value);

    const PORT_OPTIONS = ["A", "B", "C", "D"];

    React.useEffect(() => {
        setSearchValue(value);
    }, [value]);

    const handleValueChange = (val: string) => {
        setSearchValue(val);
    };

    const handleInputBlur = () => {
        combobox.closeDropdown();
        if (onBlur) {
            onBlur(searchValue);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            combobox.closeDropdown();
            onCommit(searchValue);
        }
    };

    const handleOptionSelect = (option: string) => {
        setSearchValue(option);
        onCommit(option);
        combobox.closeDropdown();
    };

    return (
        <Combobox
            store={combobox}
            withinPortal={false}
            onOptionSubmit={handleOptionSelect}
            disabled={disabled}
        >
            <Combobox.Target>
                <TextInput
                    data-testid={testId}
                    ref={portRef}
                    size="xs"
                    placeholder={isReadOnly ? "" : isPlanningMode ? "—" : "(A/B/C/D)"}
                    value={searchValue}
                    onChange={(e) => {
                        handleValueChange(e.currentTarget.value);
                        combobox.openDropdown();
                    }}
                    onClick={() => combobox.openDropdown()}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    onFocus={() => combobox.openDropdown()}
                    disabled={disabled}
                    className={classes.portInput}
                />
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {PORT_OPTIONS.map((option) => (
                        <Combobox.Option value={option} key={option}>
                            {option}
                        </Combobox.Option>
                    ))}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};


export interface PortConfigStepViewProps {
    step: PortConfigStep;
    onChange: (updates: DeepPartial<PortConfigStep>) => void;
    workflow?: TWorkflow;
}

/**
 * View component for port configuration steps.
 *
 * - count: editable in planning, session, and review_edit; read-only in review.
 * - ports (label / sub-subject): editable only in session and review_edit;
 *   disabled in planning and review.
 *
 * The ports array is kept in sync with count at render time.
 */
export const PortConfigStepView: React.FC<PortConfigStepViewProps> = ({
    step,
    onChange,
    workflow = "session",
}) => {
    const MAX_PORTS = 12;
    const PORTS_PER_COLUMN = 4;
    const stepId = step.id;
    const isPlanningMode = workflow === "planning";
    const isReadOnly = workflow === "review";

    // Count is editable everywhere except review mode
    const isCountDisabled = isReadOnly;

    // Port text inputs are only editable in session and review_edit
    const arePortsEditable = workflow === "session" || workflow === "review_edit";

    const count = Math.min(step.count ?? 2, MAX_PORTS);

    /** Produce a ports array of exactly `count` items, padding / trimming as needed. */
    const getPortsForCount = useCallback(
        (rawPorts: IPortMap[], targetCount: number): IPortMap[] => {
            const result: IPortMap[] = [...rawPorts];
            while (result.length < targetCount) {
                result.push({label: "", sub_subject: ""});
            }
            return result.slice(0, targetCount);
        },
        []
    );

    const ports = getPortsForCount(
        (step as IPortConfigStep).ports ?? [],
        count
    );

    const portsRef = useRef<IPortMap[]>(ports);
    const subSubjectRefs = useRef<(HTMLInputElement | null)[]>([]);
    const labelRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        portsRef.current = ports;
    }, [ports]);

    /** Update a single port field and propagate via onChange. */
    const commitPortField = (
        index: number,
        field: keyof IPortMap,
        value: string
    ) => {
        const updatedPorts = getPortsForCount(portsRef.current, count);
        updatedPorts[index] = {...updatedPorts[index], [field]: value};
        portsRef.current = updatedPorts;
        onChange({ports: updatedPorts} as DeepPartial<IPortConfigStep>);
    };

    const handlePortBlur = (
        index: number,
        field: keyof IPortMap,
        e: React.FocusEvent<HTMLInputElement>
    ) => {
        commitPortField(index, field, e.currentTarget.value);
    };

    const handlePortKeyDown = (
        index: number,
        field: keyof IPortMap,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key !== "Tab") {
            return;
        }

        e.preventDefault();
        commitPortField(index, field, e.currentTarget.value);
        e.currentTarget.blur();

        setTimeout(() => {
            if (field === "sub_subject") {
                if (e.shiftKey) {
                    if (index > 0) {
                        labelRefs.current[index - 1]?.focus();
                    }
                    return;
                }
                labelRefs.current[index]?.focus();
                return;
            }

            if (e.shiftKey) {
                subSubjectRefs.current[index]?.focus();
                return;
            }

            if (index < count - 1) {
                subSubjectRefs.current[index + 1]?.focus();
            }
        }, 0);
    };

    /** Handle count blur – commit the new count and adjust the ports array. */
    const handleCountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value;
        if (raw === "") {
            e.currentTarget.value = String(count);
            return;
        }
        const newCount = parseInt(raw, 10);
        if (!isNaN(newCount) && newCount > 0) {
            const boundedCount = Math.min(newCount, MAX_PORTS);
            const newPorts = getPortsForCount(portsRef.current, boundedCount);
            portsRef.current = newPorts;
            onChange({
                count: boundedCount,
                ports: newPorts,
            } as DeepPartial<PortConfigStep>);
        }
    };

    /** Render a single port row with sub-subject on left, port on right */
    const renderPortRow = (index: number) => (
        <React.Fragment key={`${stepId}-port-row-${index}`}>
            {/* Sub-subject input (left) */}
            <TextInput
                data-testid={`input-port-sub-subject-${index}`}
                size="xs"
                placeholder={isReadOnly ? "" : isPlanningMode ? "—" : "Sub-subject"}
                defaultValue={ports[index]?.sub_subject ?? ""}
                disabled={!arePortsEditable}
                onBlur={(e) => handlePortBlur(index, "sub_subject", e)}
                onKeyDown={(e) => handlePortKeyDown(index, "sub_subject", e)}
                ref={(el) => {
                    subSubjectRefs.current[index] = el;
                }}
                className={classes.subSubjectInput}
            />
            {/* Port input (right) - Combobox with predefined options + custom input */}
            <PortCombobox
                testId={`input-port-label-${index}`}
                value={ports[index]?.label ?? ""}
                disabled={!arePortsEditable}
                onCommit={(value) => commitPortField(index, "label", value)}
                onBlur={(value) => {
                    // Blur might be called even if combobox closes without selection
                    if (value !== undefined) {
                        commitPortField(index, "label", value);
                    }
                }}
                isPlanningMode={isPlanningMode}
                isReadOnly={isReadOnly}
                portRef={(el) => {
                    labelRefs.current[index] = el;
                }}
            />
        </React.Fragment>
    );

    /** Render a column set (up to 4 ports per column set) */
    const renderColumnSet = (startIndex: number) => {
        const endIndex = Math.min(startIndex + PORTS_PER_COLUMN, count);
        const columnPorts: number[] = [];
        for (let i = startIndex; i < endIndex; i++) {
            columnPorts.push(i);
        }

        return (
            <Box
                key={`column-set-${startIndex}`}
                className={classes.portColumnSet}
                data-testid={`column-set-${startIndex}`}
            >
                {/* Header row */}
                <Text size="xs" fw={500}>
                    Sub-subject
                </Text>
                <Text size="xs" fw={500}>
                    Port
                </Text>

                {/* Port rows */}
                {columnPorts.map((idx) => renderPortRow(idx))}
            </Box>
        );
    };

    return (
        <Box className={classes.container}>
            {/* Count input row */}
            <Box className={classes.countRow}>
                <div className={stepClasses.stepViewField}>
                    <Box className={classes.countInputGroup}>
                        <Text size="sm">Number of ports:</Text>
                        <NumberInput
                            data-testid="input-port-count"
                            size="xs"
                            value={count}
                            min={1}
                            max={MAX_PORTS}
                            clampBehavior="strict"
                            allowNegative={false}
                            hideControls
                            disabled={isCountDisabled}
                            onBlur={handleCountBlur}
                            className={classes.countInput}
                        />
                    </Box>
                </div>
            </Box>

            {/* Port column sets grid */}
            {count > 0 && (
                <Box className={classes.portsGridContainer}>
                    {Array.from({length: Math.ceil(count / PORTS_PER_COLUMN)}, (_, columnIndex) =>
                        renderColumnSet(columnIndex * PORTS_PER_COLUMN)
                    )}
                </Box>
            )}
        </Box>
    );
};
